import { makeAutoObservable, observable, runInAction } from "mobx"
import { nestClient, type NestClient } from "../nest/client"
import type {
    JSONValue,
    Session,
    SessionEvent,
    SessionEventAppendedNotification,
    SessionEventListResult,
    SessionTurnUpdatedNotification,
    SessionUpdatedNotification,
    TurnInput,
} from "../nest/generated"
import type { HarnessId, HarnessStreamEvent } from "../providerEvents/harnessEventTypes"
import { claudeWorkingDirectoryFromEvent, parseClaudeProviderEvent } from "../providerEvents/claude"
import { type ProviderContentPart, type ProviderUserMessage, type ProviderUserMessageKind, classifyProviderUserMessage } from "../providerEvents/userMessages"

export const SESSION_THREAD_HISTORY_EVENT_LIMIT = 5_000
export const SESSION_THREAD_HISTORY_BYTE_LIMIT = 5 * 1024 * 1024
const SESSION_THREAD_LIVE_REFRESH_LIMIT = 100
const SESSION_THREAD_LIVE_REFRESH_DELAY_MS = 150
const SESSION_THREAD_LIVE_POLL_MS = 1500
const PRIMED_SESSION_THREAD_LIMIT = 100
const PRIMED_SESSION_THREAD_MAX_AGE_MS = 10_000

export interface SessionThreadReference {
    sessionId: string
    harnessId: HarnessId
    providerSessionId?: string
    workspaceId?: string
    cwd?: string
    model?: string
}

export interface SessionThreadModelOptions {
    live?: boolean
    client?: NestClient
    onProviderWorkingDirectoryChange?: (path: string) => Promise<void>
}

export interface SessionThreadHistoryTarget {
    turnId?: string
    createdAt: string
}

export interface ThreadOrder {
    sequence?: number
    turnId?: string
    liveOrder?: number
}

export type ThreadContentPart =
    | { type: "text"; text: string }
    | {
          type: "image"
          source: { type: "nest_attachment"; attachmentId: string } | { type: "provider_uri"; uri: string }
          mediaType?: string
          filename?: string
      }
    | { type: "unknown"; raw: unknown }

export type SessionThreadItem =
    | {
          id: string
          type: "user"
          source: "provider" | "optimistic"
          kind: ProviderUserMessageKind
          parts: ThreadContentPart[]
          order: ThreadOrder
          timestamp?: string
          clientRequestId?: string
          optimisticPlacement?: "turn_start" | "inline"
      }
    | {
          id: string
          type: "provider_events"
          events: HarnessStreamEvent[]
          harnessId: HarnessId
          order: ThreadOrder
      }
    | {
          id: string
          type: "turn_status"
          turnId: string
          status: string
          order: ThreadOrder
          error?: string
      }

interface ThreadEventEnvelope {
    eventId: string
    raw: unknown
    sequence?: number
    liveOrder?: number
    receivedAt?: string
    turnId?: string
}

interface PreparedOlderHistoryPage {
    cursor: string
    result: SessionEventListResult
}

type OptimisticUserItem = Extract<SessionThreadItem, { type: "user" }>

type ThreadStreamEntry = { type: "event"; envelope: ThreadEventEnvelope } | { type: "optimistic_user"; item: OptimisticUserItem }

interface OptimisticSteeredInput {
    sessionId: string
    turnId: string
    input: TurnInput
    clientRequestId?: string
    receivedAt?: string
}

const sessionThreadModelsBySessionId = new Map<string, Set<SessionThreadModel>>()
const primedSessionThreadsBySessionId = new Map<string, PrimedSessionThread>()
const pendingSessionThreadPrimesBySessionId = new Map<string, Promise<boolean>>()

interface PrimedSessionThread {
    session?: Session
    events: SessionEvent[]
    complete: boolean
    primedAt: number
}

export function primeSessionThread(
    sessionId: string,
    snapshot: {
        session?: Session
        events: SessionEvent[]
        complete: boolean
    }
): void {
    primedSessionThreadsBySessionId.delete(sessionId)
    primedSessionThreadsBySessionId.set(sessionId, { ...snapshot, primedAt: Date.now() })
    while (primedSessionThreadsBySessionId.size > PRIMED_SESSION_THREAD_LIMIT) {
        const oldestSessionId = primedSessionThreadsBySessionId.keys().next().value
        if (!oldestSessionId) break
        primedSessionThreadsBySessionId.delete(oldestSessionId)
    }
}

function takePrimedSessionThread(sessionId: string): PrimedSessionThread | null {
    const primed = primedSessionThreadsBySessionId.get(sessionId)
    primedSessionThreadsBySessionId.delete(sessionId)
    if (!primed || Date.now() - primed.primedAt > PRIMED_SESSION_THREAD_MAX_AGE_MS) return null
    return primed
}

export function trackSessionThreadPrime(sessionId: string, promise: Promise<unknown>): void {
    const tracked = promise.then(
        () => true,
        () => false
    )
    pendingSessionThreadPrimesBySessionId.set(sessionId, tracked)
    void tracked.then(() => {
        if (pendingSessionThreadPrimesBySessionId.get(sessionId) === tracked) pendingSessionThreadPrimesBySessionId.delete(sessionId)
    })
}

export function acceptOptimisticSteeredInput(input: OptimisticSteeredInput): void {
    const models = sessionThreadModelsBySessionId.get(input.sessionId)
    if (!models) return
    for (const model of models) model.addOptimisticSteeredInput(input)
}

export function acceptSessionUpdate(session: Session): void {
    const models = sessionThreadModelsBySessionId.get(session.sessionId)
    if (!models) return
    for (const model of models) model.applySessionUpdate(session)
}

export function buildSessionThreadItems(
    reference: SessionThreadReference,
    envelopes: readonly ThreadEventEnvelope[],
    optimisticUsers: readonly OptimisticUserItem[] = []
): SessionThreadItem[] {
    const unmatchedOptimisticUsers = [...optimisticUsers]
    const sorted = [
        ...envelopes.map((envelope): ThreadStreamEntry => ({ type: "event", envelope })),
        ...optimisticUsers.map((item): ThreadStreamEntry => ({ type: "optimistic_user", item })),
    ].sort(compareThreadStreamEntries)
    const items: SessionThreadItem[] = []
    const acknowledgedOptimisticIds = new Set<string>()
    const renderedOptimisticIds = new Set<string>()
    let segmentEvents: HarnessStreamEvent[] = []
    let segmentStart: ThreadEventEnvelope | null = null
    let segmentEnd: ThreadEventEnvelope | null = null
    let previousProviderUserParts: ThreadContentPart[] | null = null
    let codexProviderTurnId: string | undefined
    const codexHumanMessageCounts = new Map<string, number>()
    let pendingGrokUser: {
        envelope: ThreadEventEnvelope
        message: ProviderUserMessage
    } | null = null

    const flushSegment = () => {
        if (!segmentStart || segmentEvents.length === 0) return
        const start = eventOrderKey(segmentStart)
        const end = segmentEnd ? eventOrderKey(segmentEnd) : start
        items.push({
            id: `${reference.sessionId}:segment:${start}:${end}`,
            type: "provider_events",
            events: segmentEvents,
            harnessId: reference.harnessId,
            order: orderFromEnvelope(segmentStart),
        })
        segmentEvents = []
        segmentStart = null
        segmentEnd = null
    }

    const appendProviderUser = (envelope: ThreadEventEnvelope, userMessage: ProviderUserMessage) => {
        const providerParts = userMessage.parts.map(threadContentPart)
        const timestamp = userMessage.timestamp ?? envelope.receivedAt
        const duplicateTextEcho = previousProviderUserParts !== null && isImageMessageTextEcho(previousProviderUserParts, providerParts)
        previousProviderUserParts = providerParts
        if (duplicateTextEcho) return
        let kind = userMessage.kind
        if (reference.harnessId === "codex") {
            const providerTurnId = userMessage.providerTurnId ?? codexProviderTurnId ?? envelope.turnId
            if (providerTurnId) {
                const priorHumanMessageCount = codexHumanMessageCounts.get(providerTurnId) ?? 0
                if (kind === "message" && priorHumanMessageCount > 0) kind = "steer"
                codexHumanMessageCounts.set(providerTurnId, priorHumanMessageCount + 1)
            }
        }
        const matchingOptimisticIndex = unmatchedOptimisticUsers.findIndex(
            (item) =>
                (item.optimisticPlacement !== "inline" && Boolean(envelope.turnId) && envelope.turnId === item.order.turnId) ||
                contentPartsMatch(item.parts, providerParts)
        )
        if (matchingOptimisticIndex >= 0) {
            const [matchingOptimistic] = unmatchedOptimisticUsers.splice(matchingOptimisticIndex, 1)
            if (matchingOptimistic && !renderedOptimisticIds.has(matchingOptimistic.id)) {
                flushSegment()
                items.push(matchingOptimistic)
                renderedOptimisticIds.add(matchingOptimistic.id)
            }
            if (matchingOptimistic) acknowledgedOptimisticIds.add(matchingOptimistic.id)
            return
        }
        flushSegment()
        items.push({
            id: `${reference.sessionId}:seq:${eventOrderKey(envelope)}:user`,
            type: "user",
            source: "provider",
            kind,
            parts: providerParts,
            order: orderFromEnvelope(envelope),
            ...(timestamp ? { timestamp } : {}),
        })
    }

    const flushGrokUser = () => {
        if (!pendingGrokUser) return
        appendProviderUser(pendingGrokUser.envelope, pendingGrokUser.message)
        pendingGrokUser = null
    }

    for (const entry of sorted) {
        if (entry.type === "optimistic_user") {
            flushGrokUser()
            if (acknowledgedOptimisticIds.has(entry.item.id)) continue
            flushSegment()
            items.push(entry.item)
            renderedOptimisticIds.add(entry.item.id)
            continue
        }

        const envelope = entry.envelope
        const codexTurnStartedId = reference.harnessId === "codex" ? codexProviderTurnStartedId(envelope.raw) : undefined
        if (codexTurnStartedId) codexProviderTurnId = codexTurnStartedId
        const userMessage = classifyProviderUserMessage(reference.harnessId, envelope.raw)
        if (reference.harnessId === "codex" && userMessage?.providerTurnId) {
            codexProviderTurnId = userMessage.providerTurnId
        }
        if ((reference.harnessId === "grok-build" || reference.harnessId === "opencode") && userMessage?.purpose === "human") {
            if (pendingGrokUser) {
                pendingGrokUser.message = {
                    ...pendingGrokUser.message,
                    parts: [...pendingGrokUser.message.parts, ...userMessage.parts],
                }
            } else {
                pendingGrokUser = { envelope, message: userMessage }
            }
            continue
        }

        flushGrokUser()
        if (userMessage?.purpose === "human") {
            appendProviderUser(envelope, userMessage)
            continue
        }

        previousProviderUserParts = null

        if (userMessage?.purpose === "context") {
            continue
        }

        if (!segmentStart) segmentStart = envelope
        segmentEnd = envelope
        segmentEvents.push(streamEventFromEnvelope(envelope, reference.harnessId, reference.sessionId))
    }

    flushGrokUser()
    flushSegment()
    return items
}

export function buildSessionThreadItemsFromSessionEvents(reference: SessionThreadReference, events: readonly SessionEvent[]): SessionThreadItem[] {
    return buildSessionThreadItems(reference, events.map(envelopeFromSessionEvent))
}

export class SessionThreadModel {
    session: Session | null = null
    items: SessionThreadItem[] = []
    loading = false
    olderLoading = false
    error: string | null = null
    hasOlder = false
    liveStatus: "idle" | "streaming" | "error" = "idle"
    lastTurnId: string | null = null
    lastTurnStatus: string | null = null
    lastTurnQueuedAt: string | null = null
    lastTurnStartedAt: string | null = null
    lastTurnError: string | null = null
    private cursor: string | undefined
    private envelopes: ThreadEventEnvelope[] = []
    private optimisticUsers = new Map<string, OptimisticUserItem>()
    private terminalTurns = new Set<string>()
    private liveOrder = 0
    private loadPromise: Promise<void> | null = null
    private olderLoadPromise: Promise<void> | null = null
    private olderPrefetchPromise: Promise<void> | null = null
    private preparedOlderPage: PreparedOlderHistoryPage | null = null
    private sessionRefreshPromise: Promise<void> | null = null
    private liveRefreshPromise: Promise<void> | null = null
    private liveRefreshQueued = false
    private liveRefreshTimer: ReturnType<typeof setTimeout> | null = null
    private livePollTimer: ReturnType<typeof setInterval> | null = null
    private disposers: Array<() => void> = []
    private subscriptionDisposer: (() => void) | null = null
    private liveGeneration = 0
    private hasCompletePrimedSnapshot = false
    private appliedPrimedSnapshot: PrimedSessionThread | null = null
    private lastLiveProviderWorkingDirectory: string | null = null
    private providerWorkingDirectoryChangeQueue = Promise.resolve()

    constructor(
        public readonly reference: SessionThreadReference,
        private readonly options: SessionThreadModelOptions = {}
    ) {
        makeAutoObservable(this, {
            reference: false,
            options: false,
            client: false,
            items: observable.ref,
            olderLoading: observable,
            envelopes: false,
            optimisticUsers: false,
            terminalTurns: false,
            disposers: false,
            subscriptionDisposer: false,
            loadPromise: false,
            olderLoadPromise: false,
            olderPrefetchPromise: false,
            preparedOlderPage: false,
            sessionRefreshPromise: false,
            liveRefreshPromise: false,
            liveRefreshTimer: false,
            livePollTimer: false,
            lastLiveProviderWorkingDirectory: false,
            providerWorkingDirectoryChangeQueue: false,
        } as never)
        const models = sessionThreadModelsBySessionId.get(reference.sessionId) ?? new Set<SessionThreadModel>()
        models.add(this)
        sessionThreadModelsBySessionId.set(reference.sessionId, models)

        const primed = takePrimedSessionThread(reference.sessionId)
        if (primed) this.applyPrimedSnapshot(primed)
    }

    applySessionUpdate(session: Session): void {
        if (session.sessionId !== this.reference.sessionId) return
        this.session = session
        if (!session.activeTurnId && session.queuedTurnIds.length === 0 && session.status !== "running") {
            this.lastTurnId = null
            this.lastTurnStatus = null
            this.lastTurnQueuedAt = null
            this.lastTurnStartedAt = null
            this.lastTurnError = null
            this.liveStatus = "idle"
        }
    }

    async loadInitial(): Promise<void> {
        const pendingPrime = pendingSessionThreadPrimesBySessionId.get(this.reference.sessionId)
        const primeSucceeded = pendingPrime ? await pendingPrime : undefined
        const primed = takePrimedSessionThread(this.reference.sessionId)
        if (primed && primed !== this.appliedPrimedSnapshot) this.applyPrimedSnapshot(primed)
        if (primeSucceeded === false) this.hasCompletePrimedSnapshot = false
        if (this.hasCompletePrimedSnapshot) {
            if (this.session?.status === "running" || this.session?.activeTurnId) this.startLivePolling()
            return
        }
        if (this.loadPromise) return this.loadPromise
        this.loadPromise = this.loadInitialPage()
        try {
            await this.loadPromise
        } finally {
            this.loadPromise = null
        }
    }

    async refreshFromNest(): Promise<void> {
        await Promise.all([this.refreshSession(), this.refreshLiveEvents()])
        if (this.session?.status === "running" || this.session?.activeTurnId) this.startLivePolling()
        else this.stopLivePolling()
    }

    private applyPrimedSnapshot(primed: PrimedSessionThread): void {
        this.session = primed.session ?? null
        this.appliedPrimedSnapshot = primed
        if (
            !primed.complete ||
            primed.events.length > SESSION_THREAD_HISTORY_EVENT_LIMIT ||
            sessionEventsRawByteLength(primed.events) > SESSION_THREAD_HISTORY_BYTE_LIMIT
        ) {
            this.envelopes = []
            this.items = []
            this.cursor = undefined
            this.hasOlder = false
            this.hasCompletePrimedSnapshot = false
            return
        }
        this.envelopes = primed.events.map(envelopeFromSessionEvent)
        this.rebuildItems()
        this.cursor = undefined
        this.hasOlder = false
        this.hasCompletePrimedSnapshot = primed.complete
    }

    loadOlder(): Promise<void> {
        if (!this.hasOlder || this.loading) return Promise.resolve()
        if (this.olderLoadPromise) return this.olderLoadPromise
        runInAction(() => {
            this.olderLoading = true
            this.error = null
        })
        const promise = this.consumeOrLoadOlderPage().finally(() => {
            runInAction(() => {
                this.olderLoading = false
                if (this.olderLoadPromise === promise) this.olderLoadPromise = null
            })
        })
        this.olderLoadPromise = promise
        return promise
    }

    async loadToHistoryTarget(target: SessionThreadHistoryTarget): Promise<string | null> {
        if (this.loading || (!this.session && this.items.length === 0)) await this.loadInitial()

        let turn: ThreadTurnSnapshot | null = null
        if (target.turnId) {
            try {
                const result = await this.client.requestMethod("session.turn.read", { turnId: target.turnId })
                if (result.turn.sessionId === this.reference.sessionId) turn = result.turn
            } catch (error) {
                console.warn("[SessionThreadModel] Failed to read a timeline target turn", {
                    sessionId: this.reference.sessionId,
                    turnId: target.turnId,
                    error,
                })
            }
        }

        while (true) {
            const match = historyTargetItem(this.reference, this.items, target.createdAt, turn, !this.hasOlder)
            if (match) return match.id
            if (!this.hasOlder) return null

            const previousCursor = this.cursor
            await this.loadOlder()
            if (this.cursor === previousCursor) return null
        }
    }

    startLive(): void {
        if (this.options.live === false) return
        if (this.disposers.length > 0) return
        const generation = ++this.liveGeneration
        this.disposers.push(
            this.client.on("session.event.appended", (params) => this.handleEventAppended(params)),
            this.client.on("session.turn.updated", (params) => this.handleTurnUpdated(params)),
            this.client.on("session.updated", (params) => this.handleSessionUpdated(params)),
            this.client.onStatusChange((status) => {
                if (status !== "connected") return
                void this.refreshSession()
                this.queueLiveEventRefresh(0)
            })
        )
        void this.client
            .retainSubscriptions([
                { method: "session.event.appended", sessionId: this.reference.sessionId },
                { method: "session.turn.updated", sessionId: this.reference.sessionId },
                { method: "session.updated", sessionId: this.reference.sessionId },
            ])
            .then((dispose) => {
                if (generation !== this.liveGeneration || this.disposers.length === 0) {
                    dispose()
                    return
                }
                this.subscriptionDisposer?.()
                this.subscriptionDisposer = dispose
            })
            .catch((error) => {
                if (generation !== this.liveGeneration) return
                console.warn("[SessionThreadModel] Failed to subscribe", { sessionId: this.reference.sessionId, error })
                runInAction(() => {
                    this.liveStatus = "error"
                })
            })
    }

    dispose(): void {
        this.liveGeneration += 1
        for (const dispose of this.disposers) dispose()
        this.disposers = []
        this.subscriptionDisposer?.()
        this.subscriptionDisposer = null
        this.clearLiveRefreshTimer()
        this.stopLivePolling()
        const models = sessionThreadModelsBySessionId.get(this.reference.sessionId)
        models?.delete(this)
        if (models?.size === 0) sessionThreadModelsBySessionId.delete(this.reference.sessionId)
    }

    addOptimisticSteeredInput(input: OptimisticSteeredInput): void {
        if (input.sessionId !== this.reference.sessionId) return
        const item = optimisticUserItemFromInput(this.reference, input.turnId, input.input, ++this.liveOrder, {
            clientRequestId: input.clientRequestId,
            timestamp: input.receivedAt,
            placement: "inline",
        })
        if (!item) return

        const key = input.clientRequestId ?? `${input.turnId}:steer:${this.liveOrder}`
        runInAction(() => {
            this.optimisticUsers.set(key, item)
            this.rebuildItems()
            this.liveStatus = "streaming"
        })
    }

    private async loadInitialPage(): Promise<void> {
        runInAction(() => {
            this.loading = true
            this.error = null
        })
        try {
            const [sessionResult, eventResult] = await Promise.all([
                this.client.requestMethod("session.read", { sessionId: this.reference.sessionId }),
                this.requestHistoryPage(),
            ])
            const nextEnvelopes = eventResult.items.map(envelopeFromSessionEvent)
            runInAction(() => {
                this.session = sessionResult.session
                this.envelopes = mergeEnvelopes(this.envelopes, nextEnvelopes)
                this.rebuildItems()
                this.cursor = eventResult.nextCursor
                this.hasOlder = eventResult.hasMore
                this.loading = false
                this.liveStatus = "idle"
            })
            if (sessionResult.session.status === "running" || sessionResult.session.activeTurnId) {
                this.startLivePolling()
            }
            this.prepareOlderPage()
        } catch (error) {
            this.handleHistoryLoadError(error)
            runInAction(() => {
                this.loading = false
            })
        }
    }

    private async consumeOrLoadOlderPage(): Promise<void> {
        if (this.olderPrefetchPromise) await this.olderPrefetchPromise

        const prepared = this.preparedOlderPage
        if (!prepared || prepared.cursor !== this.cursor) {
            try {
                const result = await this.requestHistoryPage(this.cursor)
                this.applyOlderPage(result)
                this.prepareOlderPage()
            } catch (error) {
                this.handleHistoryLoadError(error)
            }
            return
        }

        this.preparedOlderPage = null
        this.applyOlderPage(prepared.result)
        this.prepareOlderPage()
    }

    private applyOlderPage(result: SessionEventListResult): void {
        const nextEnvelopes = result.items.map(envelopeFromSessionEvent)
        runInAction(() => {
            this.envelopes = mergeEnvelopes(this.envelopes, nextEnvelopes)
            this.rebuildItems()
            this.cursor = result.nextCursor
            this.hasOlder = result.hasMore
            this.liveStatus = "idle"
        })
    }

    private prepareOlderPage(): void {
        const cursor = this.cursor
        if (!this.hasOlder || !cursor || this.preparedOlderPage || this.olderPrefetchPromise) return

        const promise = this.requestHistoryPage(cursor)
            .then((result) => {
                if (this.cursor !== cursor || !this.hasOlder) return
                this.preparedOlderPage = { cursor, result }
            })
            .catch((error) => {
                console.warn("[SessionThreadModel] Failed to prepare older session history", { sessionId: this.reference.sessionId, error })
            })
            .finally(() => {
                if (this.olderPrefetchPromise === promise) this.olderPrefetchPromise = null
            })
        this.olderPrefetchPromise = promise
    }

    private requestHistoryPage(cursor?: string): Promise<SessionEventListResult> {
        return this.client.requestMethod("session.event.list", {
            sessionId: this.reference.sessionId,
            limit: SESSION_THREAD_HISTORY_EVENT_LIMIT,
            maxBytes: SESSION_THREAD_HISTORY_BYTE_LIMIT,
            ...(cursor ? { cursor } : {}),
        })
    }

    private handleHistoryLoadError(error: unknown): void {
        const message = error instanceof Error ? error.message : "Failed to load session thread"
        console.warn("[SessionThreadModel] Failed to load session thread", { sessionId: this.reference.sessionId, error })
        runInAction(() => {
            this.error = message
            this.liveStatus = "error"
        })
    }

    private handleEventAppended(params: SessionEventAppendedNotification | undefined): void {
        if (!params || params.sessionId !== this.reference.sessionId) return
        const envelope = envelopeFromSessionEvent(params.event)
        runInAction(() => {
            this.envelopes = mergeEnvelopes(this.envelopes, [envelope])
            this.rebuildItems()
            this.liveStatus = "streaming"
        })
        this.applyLiveProviderControlEvents([envelope])
        this.startLivePolling()
        this.queueLiveEventRefresh()
    }

    private handleSessionUpdated(params: SessionUpdatedNotification | undefined): void {
        if (!params || params.sessionId !== this.reference.sessionId) return
        void this.refreshSession()
    }

    private refreshSession(): Promise<void> {
        if (this.sessionRefreshPromise) return this.sessionRefreshPromise
        const promise = this.client
            .requestMethod("session.read", { sessionId: this.reference.sessionId })
            .then((result) => {
                this.applySessionUpdate(result.session)
            })
            .catch((error) => {
                console.warn("[SessionThreadModel] Failed to refresh session", { sessionId: this.reference.sessionId, error })
            })
            .finally(() => {
                if (this.sessionRefreshPromise === promise) this.sessionRefreshPromise = null
            })
        this.sessionRefreshPromise = promise
        return promise
    }

    private handleTurnUpdated(params: SessionTurnUpdatedNotification | undefined): void {
        if (!params || params.sessionId !== this.reference.sessionId) return
        const turnId = params.turnId
        const turn = turnFromNotification(params.turn)
        const optimisticUser = turn ? optimisticUserItemFromTurn(this.reference, turn, ++this.liveOrder) : null
        const status =
            params.status ?? turn?.status ?? (params.cancelled ? "cancelled" : params.interrupted ? "stopped" : params.completed ? "completed" : undefined)
        if (!status) return
        const terminal = isTerminalTurnStatus(status)
        runInAction(() => {
            if (this.lastTurnId !== turnId) {
                this.lastTurnQueuedAt = null
                this.lastTurnStartedAt = null
            }
            this.lastTurnId = turnId
            this.lastTurnStatus = status
            this.lastTurnQueuedAt = turn?.queuedAt ?? this.lastTurnQueuedAt
            this.lastTurnStartedAt = turn?.startedAt ?? this.lastTurnStartedAt
            this.lastTurnError = params.error ?? turn?.error ?? null
            if (optimisticUser) {
                this.optimisticUsers.set(turnId, optimisticUser)
                this.rebuildItems()
            }
            if (terminal) {
                this.terminalTurns.add(turnId)
                if (status === "cancelled") this.optimisticUsers.delete(turnId)
                this.items = this.items.filter((item) => !(item.type === "turn_status" && item.turnId === turnId))
                this.liveStatus = status === "error" ? "error" : "idle"
                return
            }
            if (this.terminalTurns.has(turnId)) {
                this.liveStatus = "idle"
                return
            }
            this.items = upsertTurnStatus(this.items, {
                id: `turn:${turnId}:status`,
                type: "turn_status",
                turnId,
                status,
                order: { turnId },
                ...(params.error ? { error: params.error } : {}),
            })
            this.liveStatus = status === "running" || status === "queued" ? "streaming" : "idle"
        })
        if (terminal) {
            this.stopLivePolling()
            this.queueLiveEventRefresh(0)
        } else if (status === "running" || status === "waiting" || status === "queued") {
            this.startLivePolling()
            this.queueLiveEventRefresh()
        }
    }

    private queueLiveEventRefresh(delayMs = SESSION_THREAD_LIVE_REFRESH_DELAY_MS): void {
        if (this.liveRefreshTimer) return
        this.liveRefreshTimer = setTimeout(() => {
            this.liveRefreshTimer = null
            void this.refreshLiveEvents()
        }, delayMs)
    }

    private async refreshLiveEvents(): Promise<void> {
        if (this.liveRefreshPromise) {
            this.liveRefreshQueued = true
            return this.liveRefreshPromise
        }
        this.liveRefreshPromise = this.loadNewNativeEvents()
        try {
            await this.liveRefreshPromise
        } finally {
            this.liveRefreshPromise = null
            if (this.liveRefreshQueued) {
                this.liveRefreshQueued = false
                this.queueLiveEventRefresh(0)
            }
        }
    }

    private async loadNewNativeEvents(): Promise<void> {
        try {
            const afterSequence = latestSequence(this.envelopes)
            const eventResult = await this.client.requestMethod("session.event.list", {
                sessionId: this.reference.sessionId,
                limit: SESSION_THREAD_LIVE_REFRESH_LIMIT,
                ...(afterSequence > 0 ? { afterSequence } : {}),
            })
            const nextEnvelopes = eventResult.items.map(envelopeFromSessionEvent)
            if (nextEnvelopes.length > 0) {
                runInAction(() => {
                    this.envelopes = mergeEnvelopes(this.envelopes, nextEnvelopes)
                    this.rebuildItems()
                    this.liveStatus = "streaming"
                })
                if (afterSequence > 0) this.applyLiveProviderControlEvents([...nextEnvelopes].sort(compareThreadEvents))
            }
            if (eventResult.hasMore) this.queueLiveEventRefresh(0)
        } catch (error) {
            console.warn("[SessionThreadModel] Failed to refresh live session events", { sessionId: this.reference.sessionId, error })
            runInAction(() => {
                this.liveStatus = "error"
            })
        }
    }

    private startLivePolling(): void {
        if (this.options.live === false) return
        if (this.livePollTimer) return
        this.livePollTimer = setInterval(() => {
            this.queueLiveEventRefresh(0)
        }, SESSION_THREAD_LIVE_POLL_MS)
    }

    private stopLivePolling(): void {
        if (!this.livePollTimer) return
        clearInterval(this.livePollTimer)
        this.livePollTimer = null
    }

    private get client(): NestClient {
        return this.options.client ?? nestClient
    }

    private clearLiveRefreshTimer(): void {
        if (!this.liveRefreshTimer) return
        clearTimeout(this.liveRefreshTimer)
        this.liveRefreshTimer = null
    }

    private rebuildItems(): void {
        const nextItems = buildSessionThreadItems(this.reference, this.envelopes, [...this.optimisticUsers.values()])
        this.items = reuseUnchangedThreadItems(this.items, nextItems)
    }

    private applyLiveProviderControlEvents(envelopes: readonly ThreadEventEnvelope[]): void {
        if (this.reference.harnessId !== "claude-code") return
        const callback = this.options.onProviderWorkingDirectoryChange
        if (!callback) return

        for (const envelope of envelopes) {
            const event = parseClaudeProviderEvent(envelope.raw)
            if (event?.type !== "relocated" && event?.type !== "worktree-state") continue

            const path = claudeWorkingDirectoryFromEvent(event)
            if (!path || path === this.lastLiveProviderWorkingDirectory) continue
            this.lastLiveProviderWorkingDirectory = path
            this.providerWorkingDirectoryChangeQueue = this.providerWorkingDirectoryChangeQueue
                .then(() => callback(path))
                .catch((error) => {
                    if (this.lastLiveProviderWorkingDirectory === path) this.lastLiveProviderWorkingDirectory = null
                    console.warn("[SessionThreadModel] Failed to apply provider working directory", {
                        sessionId: this.reference.sessionId,
                        path,
                        error,
                    })
                })
        }
    }
}

export interface ThreadTurnSnapshot {
    turnId: string
    status: string
    input: JSONValue
    clientRequestId?: string
    queuedAt?: string
    startedAt?: string
    error?: string
}

export function optimisticUserItemFromTurn(reference: SessionThreadReference, turn: ThreadTurnSnapshot, liveOrder: number): OptimisticUserItem | null {
    if (turn.status === "queued" || turn.status === "cancelled") return null
    return optimisticUserItemFromInput(reference, turn.turnId, turn.input, liveOrder, {
        clientRequestId: turn.clientRequestId,
        timestamp: turn.startedAt ?? turn.queuedAt,
        placement: "turn_start",
    })
}

export function optimisticUserItemFromInput(
    reference: SessionThreadReference,
    turnId: string,
    input: JSONValue | TurnInput,
    liveOrder: number,
    options: { clientRequestId?: string; timestamp?: string; placement: "turn_start" | "inline" }
): OptimisticUserItem | null {
    const parts = threadContentPartsFromTurnInput(input)
    if (parts.length === 0) return null
    return {
        id: `${reference.sessionId}:input:${options.clientRequestId ?? turnId}`,
        type: "user",
        source: "optimistic",
        kind: options.placement === "inline" ? "steer" : "message",
        parts,
        order: { turnId, liveOrder },
        optimisticPlacement: options.placement,
        ...(options.clientRequestId ? { clientRequestId: options.clientRequestId } : {}),
        ...(options.timestamp ? { timestamp: options.timestamp } : {}),
    }
}

function envelopeFromSessionEvent(event: SessionEvent): ThreadEventEnvelope {
    return {
        eventId: event.eventId,
        raw: event.raw,
        sequence: event.sequence,
        receivedAt: event.receivedAt,
        turnId: event.turnId,
    }
}

function sessionEventsRawByteLength(events: readonly SessionEvent[]): number {
    const encoder = new TextEncoder()
    let total = 0
    for (const event of events) {
        const raw = JSON.stringify(event.raw)
        if (raw) total += encoder.encode(raw).byteLength
        if (total > SESSION_THREAD_HISTORY_BYTE_LIMIT) return total
    }
    return total
}

function mergeEnvelopes(existing: ThreadEventEnvelope[], next: ThreadEventEnvelope[]): ThreadEventEnvelope[] {
    const byId = new Map(existing.map((event) => [event.eventId, event]))
    for (const event of next) byId.set(event.eventId, event)
    return [...byId.values()].sort(compareThreadEvents)
}

function reuseUnchangedThreadItems(existing: SessionThreadItem[], next: SessionThreadItem[]): SessionThreadItem[] {
    const existingById = new Map(existing.map((item) => [item.id, item]))
    let changed = existing.length !== next.length
    const reconciled = next.map((nextItem, index) => {
        const existingItem = existingById.get(nextItem.id)
        if (!existingItem || !threadItemsEqual(existingItem, nextItem)) {
            changed = true
            return nextItem
        }
        if (existing[index] !== existingItem) changed = true
        return existingItem
    })
    return changed ? reconciled : existing
}

function threadItemsEqual(existing: SessionThreadItem, next: SessionThreadItem): boolean {
    if (existing.type === "provider_events" && next.type === "provider_events") {
        return (
            existing.harnessId === next.harnessId &&
            threadOrdersEqual(existing.order, next.order) &&
            existing.events.length === next.events.length &&
            existing.events.every((event, index) => providerEventsEqual(event, next.events[index]))
        )
    }
    if (existing.type === "user" && next.type === "user") {
        return (
            existing.source === next.source &&
            existing.kind === next.kind &&
            existing.timestamp === next.timestamp &&
            existing.clientRequestId === next.clientRequestId &&
            existing.optimisticPlacement === next.optimisticPlacement &&
            threadOrdersEqual(existing.order, next.order) &&
            existing.parts.length === next.parts.length &&
            existing.parts.every((part, index) => threadContentPartsEqual(part, next.parts[index]))
        )
    }
    if (existing.type === "turn_status" && next.type === "turn_status") {
        return (
            existing.turnId === next.turnId && existing.status === next.status && existing.error === next.error && threadOrdersEqual(existing.order, next.order)
        )
    }
    return false
}

function providerEventsEqual(existing: HarnessStreamEvent, next: HarnessStreamEvent | undefined): boolean {
    if (!next || existing.direction !== "execution" || next.direction !== "execution" || existing.type !== "raw_message" || next.type !== "raw_message") {
        return false
    }
    return existing.id === next.id && existing.executionId === next.executionId && existing.harnessId === next.harnessId && existing.message === next.message
}

function threadOrdersEqual(existing: ThreadOrder, next: ThreadOrder): boolean {
    return existing.sequence === next.sequence && existing.turnId === next.turnId && existing.liveOrder === next.liveOrder
}

function threadContentPartsEqual(existing: ThreadContentPart, next: ThreadContentPart | undefined): boolean {
    if (!next || existing.type !== next.type) return false
    if (existing.type === "text" && next.type === "text") return existing.text === next.text
    if (existing.type === "unknown" && next.type === "unknown") return existing.raw === next.raw
    if (existing.type === "image" && next.type === "image") {
        if (existing.source.type === "nest_attachment" && next.source.type === "nest_attachment") {
            return existing.source.attachmentId === next.source.attachmentId && existing.mediaType === next.mediaType && existing.filename === next.filename
        }
        if (existing.source.type === "provider_uri" && next.source.type === "provider_uri") {
            return existing.source.uri === next.source.uri && existing.mediaType === next.mediaType && existing.filename === next.filename
        }
    }
    return false
}

function latestSequence(envelopes: readonly ThreadEventEnvelope[]): number {
    return envelopes.reduce((latest, envelope) => (envelope.sequence !== undefined ? Math.max(latest, envelope.sequence) : latest), 0)
}

function upsertTurnStatus(items: SessionThreadItem[], statusItem: Extract<SessionThreadItem, { type: "turn_status" }>): SessionThreadItem[] {
    const without = items.filter((item) => item.id !== statusItem.id)
    return [...without, statusItem]
}

function streamEventFromEnvelope(envelope: ThreadEventEnvelope, harnessId: HarnessId, executionId: string): HarnessStreamEvent {
    return {
        id: envelope.eventId,
        type: "raw_message",
        direction: "execution",
        executionId,
        harnessId,
        message: envelope.raw,
    }
}

function threadContentPart(part: ProviderContentPart): ThreadContentPart {
    switch (part.type) {
        case "text":
            return part
        case "image":
            return {
                type: "image",
                source: { type: "provider_uri", uri: part.uri },
                ...(part.mediaType ? { mediaType: part.mediaType } : {}),
            }
        case "unknown":
            return part
    }
}

function threadContentPartsFromTurnInput(input: JSONValue | TurnInput): ThreadContentPart[] {
    if (typeof input === "string") return textContentParts(input)
    const record = recordValue(input)
    if (!record) return []
    const parts = Array.isArray(record.parts) ? record.parts : []
    const out: ThreadContentPart[] = []
    for (const part of parts) {
        const partRecord = recordValue(part)
        if (!partRecord) continue
        const type = stringValue(partRecord.type)
        if (type === "text") {
            out.push(...textContentParts(stringValue(partRecord.text)))
            continue
        }
        if (type === "attachment") {
            const attachmentId = stringValue(partRecord.attachmentId)
            if (!attachmentId) {
                out.push({ type: "unknown", raw: part })
                continue
            }
            out.push({
                type: "image",
                source: { type: "nest_attachment", attachmentId },
                ...(stringValue(partRecord.mediaType) ? { mediaType: stringValue(partRecord.mediaType) } : {}),
                ...(stringValue(partRecord.filename) ? { filename: stringValue(partRecord.filename) } : {}),
            })
        }
    }
    return out
}

function textContentParts(text: string | undefined): ThreadContentPart[] {
    const trimmed = text?.trim()
    return trimmed ? [{ type: "text", text: trimmed }] : []
}

function orderFromEnvelope(envelope: ThreadEventEnvelope): ThreadOrder {
    return {
        ...(envelope.sequence !== undefined ? { sequence: envelope.sequence } : {}),
        ...(envelope.turnId ? { turnId: envelope.turnId } : {}),
        ...(envelope.liveOrder !== undefined ? { liveOrder: envelope.liveOrder } : {}),
    }
}

function isTerminalTurnStatus(status: string): boolean {
    return status === "completed" || status === "cancelled" || status === "stopped" || status === "error"
}

function compareThreadEvents(a: ThreadEventEnvelope, b: ThreadEventEnvelope): number {
    const aKey = eventSortKey(a)
    const bKey = eventSortKey(b)
    if (aKey !== bKey) return aKey - bKey
    return a.eventId.localeCompare(b.eventId)
}

function compareThreadStreamEntries(a: ThreadStreamEntry, b: ThreadStreamEntry): number {
    const turnRelativeOrder = compareSameTurnEntries(a, b)
    if (turnRelativeOrder !== 0) return turnRelativeOrder
    const aKey = streamEntrySortKey(a)
    const bKey = streamEntrySortKey(b)
    if (aKey !== bKey) return aKey - bKey
    if (a.type !== b.type) return a.type === "event" ? -1 : 1
    if (a.type === "event" && b.type === "event") return a.envelope.eventId.localeCompare(b.envelope.eventId)
    if (a.type === "optimistic_user" && b.type === "optimistic_user") return a.item.id.localeCompare(b.item.id)
    return 0
}

function compareSameTurnEntries(a: ThreadStreamEntry, b: ThreadStreamEntry): number {
    const aTurnId = streamEntryTurnId(a)
    const bTurnId = streamEntryTurnId(b)
    if (!aTurnId || aTurnId !== bTurnId || a.type === b.type) return 0
    const optimistic = a.type === "optimistic_user" ? a.item : b.type === "optimistic_user" ? b.item : null
    if (optimistic?.optimisticPlacement !== "turn_start") return 0
    return a.type === "optimistic_user" ? -1 : 1
}

function streamEntryTurnId(entry: ThreadStreamEntry): string | undefined {
    return entry.type === "event" ? entry.envelope.turnId : entry.item.order.turnId
}

function streamEntrySortKey(entry: ThreadStreamEntry): number {
    return entry.type === "event" ? eventSortKey(entry.envelope) : orderSortKey(entry.item.order)
}

function eventSortKey(envelope: ThreadEventEnvelope): number {
    return orderSortKey(orderFromEnvelope(envelope))
}

function orderSortKey(order: ThreadOrder): number {
    return order.sequence ?? 1_000_000_000 + (order.liveOrder ?? 0)
}

function eventOrderKey(envelope: ThreadEventEnvelope): string {
    if (envelope.sequence !== undefined) return String(envelope.sequence)
    if (envelope.liveOrder !== undefined) return `live-${envelope.liveOrder}`
    return envelope.eventId
}

function turnFromNotification(value: JSONValue | undefined): ThreadTurnSnapshot | null {
    const record = recordValue(value)
    if (!record) return null
    const turnId = stringValue(record.turnId)
    const status = stringValue(record.status)
    if (!turnId || !status || !("input" in record)) return null
    return {
        turnId,
        status,
        input: record.input,
        ...(stringValue(record.clientRequestId) ? { clientRequestId: stringValue(record.clientRequestId) } : {}),
        ...(stringValue(record.queuedAt) ? { queuedAt: stringValue(record.queuedAt) } : {}),
        ...(stringValue(record.startedAt) ? { startedAt: stringValue(record.startedAt) } : {}),
        ...(stringValue(record.error) ? { error: stringValue(record.error) } : {}),
    }
}

function textFromThreadParts(parts: readonly ThreadContentPart[]): string {
    return parts
        .map((part) => (part.type === "text" ? part.text : ""))
        .join("\n")
        .replace(/\s+/g, " ")
        .trim()
}

const HISTORY_TARGET_TIME_TOLERANCE_MS = 5 * 60 * 1_000

function historyTargetItem(
    reference: SessionThreadReference,
    items: readonly SessionThreadItem[],
    createdAt: string,
    turn: ThreadTurnSnapshot | null,
    historyExhausted: boolean
): OptimisticUserItem | null {
    const users = items.filter((item): item is OptimisticUserItem => item.type === "user")
    if (users.length === 0) return null

    if (turn) {
        const exact = users.find(
            (item) => item.order.turnId === turn.turnId || (turn.clientRequestId !== undefined && item.clientRequestId === turn.clientRequestId)
        )
        if (exact) return exact
    }

    const targetTime = parsedTimestamp(turn?.startedAt ?? turn?.queuedAt ?? createdAt)
    const expected = turn ? optimisticUserItemFromTurn(reference, turn, 0) : null
    const contentMatches = expected ? users.filter((item) => contentPartsMatch(expected.parts, item.parts)) : []
    const closestContentMatch = closestTimestampedUser(contentMatches, targetTime)
    const closestContentMatchTime = parsedTimestamp(closestContentMatch?.timestamp)
    if (
        closestContentMatch &&
        targetTime !== null &&
        closestContentMatchTime !== null &&
        Math.abs(closestContentMatchTime - targetTime) <= HISTORY_TARGET_TIME_TOLERANCE_MS
    ) {
        return closestContentMatch
    }

    const timestampedUsers = users.flatMap((item) => {
        const timestamp = parsedTimestamp(item.timestamp)
        return timestamp === null ? [] : [{ item, timestamp }]
    })
    const oldestLoadedTime = timestampedUsers.reduce<number | null>(
        (oldest, candidate) => (oldest === null ? candidate.timestamp : Math.min(oldest, candidate.timestamp)),
        null
    )
    const targetCovered = targetTime !== null && oldestLoadedTime !== null && oldestLoadedTime <= targetTime
    if (!historyExhausted && !targetCovered) return null

    return closestContentMatch ?? closestTimestampedUser(users, targetTime)
}

function closestTimestampedUser(items: readonly OptimisticUserItem[], targetTime: number | null): OptimisticUserItem | null {
    if (targetTime === null) return null
    let closest: { item: OptimisticUserItem; distance: number } | null = null
    for (const item of items) {
        const timestamp = parsedTimestamp(item.timestamp)
        if (timestamp === null) continue
        const distance = Math.abs(timestamp - targetTime)
        if (!closest || distance < closest.distance) closest = { item, distance }
    }
    return closest?.item ?? null
}

function parsedTimestamp(value: string | undefined): number | null {
    if (!value) return null
    const timestamp = Date.parse(value)
    return Number.isFinite(timestamp) ? timestamp : null
}

function contentPartsMatch(optimisticParts: readonly ThreadContentPart[], providerParts: readonly ThreadContentPart[]): boolean {
    const optimisticText = textFromThreadParts(optimisticParts)
    const providerText = textFromThreadParts(providerParts)
    if (!optimisticText || !providerText) return false
    return optimisticText === providerText || providerText.includes(optimisticText) || optimisticText.includes(providerText)
}

function isImageMessageTextEcho(previousParts: readonly ThreadContentPart[], currentParts: readonly ThreadContentPart[]): boolean {
    if (!previousParts.some((part) => part.type === "image") || currentParts.some((part) => part.type !== "text")) return false
    const previousText = textFromThreadParts(previousParts)
    return previousText.length > 0 && previousText === textFromThreadParts(currentParts)
}

function codexProviderTurnStartedId(raw: unknown): string | undefined {
    const record = recordValue(raw)
    if (!record || stringValue(record.type) !== "event_msg") return undefined
    const payload = recordValue(record.payload)
    if (!payload || stringValue(payload.type) !== "task_started") return undefined
    return stringValue(payload.turn_id)
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined
}
